import { CheckBadgeIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { trackEvent } from 'helpers/analytics'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../../components/tailwind/Button'
import { Heading } from '../../../../components/ui/Heading'

function CompanyProfileAdoptionMessage({
  brandData,
  onAdopt,
  onDecline,
  className
}) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isAdopted, setIsAdopted] = useState(false)
  const previewUrlRef = useRef(null)

  // Safe access to searchData
  const searchItem = brandData?.searchData?.[0]
  const companyName =
    searchItem?.name || searchItem?.domain || brandData?.name || ''
  const companyIcon = searchItem?.icon || searchItem?.logo
  const isVerified = searchItem?.verified || false

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
        previewUrlRef.current = null
      }
    }
  }, [])

  // Early return if no valid brand data (after all hooks)
  if (!brandData || !searchItem) {
    return (
      <div
        className={clsx(
          'w-full rounded-lg border border-lightGrey bg-paleWhiteBlue/40 p-4',
          className
        )}
      >
        <Heading level={5}>
          {t('discover.assistant.company_profile_adoption.title')}
        </Heading>
        <p className='mt-1 text-sm font-normal text-darkBlack'>
          {t(
            'discover.assistant.company_profile_adoption.no_data',
            'Keine Firmendaten verfügbar.'
          )}
        </p>
      </div>
    )
  }

  const handleAdopt = async () => {
    setIsLoading(true)
    try {
      trackEvent('discover_accept_company_profile', {
        company_name: brandData.name || companyName,
        // Vorschlag = Logo + Firmenname zusammen; Brandfetch findet nicht
        // immer ein Logo → unterscheidbar machen, was übernommen wurde.
        logo_present: !!brandData?.searchData?.[0]?.icon
      })
      await onAdopt(brandData)
      setIsAdopted(true)
    } catch (error) {
      console.error('Error adopting company profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDecline = () => {
    trackEvent('discover_decline_company_profile', {
      company_name: brandData.name || companyName,
      logo_present: !!brandData?.searchData?.[0]?.icon
    })
    setIsAdopted(true)
    onDecline()
  }

  return (
    <div
      className={clsx(
        'w-full rounded-lg border border-lightGrey bg-paleWhiteBlue/40 p-4',
        className
      )}
    >
      <Heading level={5}>
        {t('discover.assistant.company_profile_adoption.title')}
      </Heading>

      <p className='mt-1 text-sm font-normal text-darkBlack'>
        {t('discover.assistant.company_profile_adoption.description', {
          companyName
        })}
      </p>

      <div className='mt-4 flex items-start gap-4 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4'>
        {companyIcon && (
          <div className='flex-shrink-0'>
            <img
              src={companyIcon}
              alt='Company Logo'
              className='size-16 rounded-lg border bg-white object-contain p-1 shadow-sm transition-transform hover:scale-105'
            />
          </div>
        )}

        <div className='min-w-0 flex-grow'>
          <div className='mb-1 flex items-center gap-2'>
            <Heading level={4} className='truncate'>
              {brandData.name || companyName}
            </Heading>
            {isVerified && (
              <CheckBadgeIcon
                title={t(
                  'discover.assistant.company_profile_adoption.verified_badge_tooltip'
                )}
                className='h-5 w-5 flex-shrink-0 text-green-500'
              />
            )}
          </div>
          <p className='text-sm text-gray-600'>
            {t('discover.assistant.company_profile_adoption.card_description')}
          </p>
        </div>
      </div>

      {!isAdopted && (
        <div className='mt-6 flex flex-col gap-2'>
          <Button.PrimaryBase
            text={t('discover.assistant.company_profile_adoption.adopt_button')}
            onClick={handleAdopt}
            disabled={isLoading}
            className='flex-1'
          />
          <Button.SecondaryBase
            text={t(
              'discover.assistant.company_profile_adoption.decline_button'
            )}
            onClick={handleDecline}
            disabled={isLoading}
            className='flex-1'
          />
          <span className='text-xs text-gray-500'>
            {t('discover.assistant.company_profile_adoption.settings_hint')}
          </span>
        </div>
      )}
    </div>
  )
}

CompanyProfileAdoptionMessage.propTypes = {
  brandData: PropTypes.shape({
    name: PropTypes.string,
    logoUrl: PropTypes.string,
    searchData: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        domain: PropTypes.string,
        icon: PropTypes.string,
        logo: PropTypes.string,
        verified: PropTypes.bool
      })
    )
  }),
  onAdopt: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  className: PropTypes.string
}

export default CompanyProfileAdoptionMessage
